import React, { useMemo, useState } from 'react';

import { downloadUrl, downloadZip } from '@apis/common';
import { AddTransportStatusDialog } from '@components/admin/booking/AddTransportStatusDialog';
import { Button } from '@components/Button';
import { QueryContainer } from '@components/QueryContainer';
import { SpinnerModal } from '@components/SpinnerModal';
import { TrackingDetailItemNote } from '@components/utilities/CargoTracking/TrackingDetailItemNote';
import { arrivalNoticeStrings } from '@data/arrivalNotice';
import { useDeleteTrackingData as useDashboardDeleteTrackingData } from '@hooks/mutation/dashboard/useDeleteTrackingData';
import { usePatchTrackingData as useDashboardPatchTrackingData } from '@hooks/mutation/dashboard/usePatchTrackingData';
import { useTrackingData as useDashboardTrackingData } from '@hooks/queries/dashboard/useTrackingData';
import { useTrackingData } from '@hooks/queries/useTrackingData';
import { useConfirmDialog } from '@hooks/useActionableDialog';
import { PreAlertDetails } from '@models/next/PreAlert';
import {
  DomesticTrackingDetailItem,
  TrackingDataDeleteQuery,
  TrackingDetailItem,
  TransportStatus,
} from '@models/TrackingData';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { foldMessagesEither, MessagesEither } from '@utils/errors';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { eq, formatISOString, stringifySearch, toJST } from '@utils/types';
import clsx from 'clsx';
import { trackingDataMeta } from '@data/cargoTracking';

const strings = createStrings({
  ja: {
    status: 'ステータス',
    dateTime: '日時',
    note: '備考',
    document: '書類',
    addTransportStatus: '輸送状況を追加する',
    notFound: '追跡データは作成されていません。',
    barCode: 'ラベル',
    preAlert: 'Pre-Alert',
    invoice: '請求書',
    individualInsurance: '個別保険',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  link: {
    color: theme.palette.primary.main,
  },
  tableRow: {
    height: '2.8rem',
    '&:first-child > td': {
      ...common.fontWeight600Important,
    },
  },
  trackingDataDetailsTable: {
    ...common.middleText,
    display: 'grid',
    gridTemplateColumns: 'max-content max-content 1fr max-content max-content',
    '& > div > div': {
      padding: '0.75rem',
      borderBottom: `1px solid ${theme.palette.grey[200]}`,
    },
  },
  trackingDataDetailsHead: {
    backgroundColor: theme.palette.base[200],
  },
  trackingDataDetailsCell: {
    ...common.textGray700,
  },
}));

export interface TrackingDataDetailsProps {
  bookingId: number;
  d2dNo: string;
  preAlert: PreAlertDetails;
  isClient: boolean;
  canEdit?: MessagesEither;
}

export const TrackingDataDetails: React.FC<TrackingDataDetailsProps> = (props) => {
  const { bookingId, d2dNo, preAlert, isClient, canEdit } = props;

  const editable = canEdit?._tag === 'Right';

  const { confirm, renderDialog } = useConfirmDialog();

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  let trackingData: ReturnType<typeof useDashboardTrackingData>;
  if (isClient) {
    trackingData = useTrackingData(d2dNo);
  } else {
    trackingData = useDashboardTrackingData(d2dNo);
  }

  const dashboardPatchTrackingData = useDashboardPatchTrackingData(d2dNo);
  const dashboardDeleteTrackingData = useDashboardDeleteTrackingData(d2dNo);

  const isLoading = dashboardPatchTrackingData.isLoading || dashboardDeleteTrackingData.isLoading;

  const [dialogType, setDialogType] = useState<'add' | 'edit'>('add');
  const [editingTransportStatus, setEditingTransportStatus] = useState<TransportStatus>();
  const [editingItem, setEditingItem] = useState<TrackingDetailItem | DomesticTrackingDetailItem>();
  const [dialogOpen, setDialogOpen] = useState(false);

  const getFileLink = (file: string[] | undefined | null, fileName: string) => {
    if (!file) return '';
    return file.length === 1 ? (
      <a
        onClick={() => downloadUrl({ name: fileName, url: file[0], download: false })}
        target="_blank"
        rel="noreferrer"
        className={clsx(classes.link, classes.baseButton)}
      >
        {fileName || strings.document}
      </a>
    ) : (
      <a
        target="_blank"
        rel="noreferrer"
        className={clsx(classes.link, classes.baseButton)}
        onClick={() => {
          downloadZip(file, strings.document);
        }}
      >
        {fileName || strings.document}
      </a>
    );
  };

  const onClickEdit = (
    statusName: TransportStatus,
    item: TrackingDetailItem | DomesticTrackingDetailItem,
  ) => {
    setEditingTransportStatus(statusName);
    setDialogType('edit');
    setEditingItem(item);
    setDialogOpen(true);
  };

  const renderTrackingDetailItem = (
    statusName: TransportStatus,
    item: TrackingDetailItem | DomesticTrackingDetailItem,
    i: number,
  ) => {
    if (['請求書全部発行完了', '支払伝票全部発行完了'].includes(statusName) && isClient) {
      return null;
    }

    const timeFormatter = toJST;

    const preAlertUrl = `${
      isClient ? url('/utilities/Bookings/ExportPreAlert') : url('/admin/booking/ExportPreAlert')
    }?${stringifySearch({
      d2dNo,
    })}`;

    const printBarcodeUrl = `${
      isClient ? url('/utilities/Bookings/PrintBarcode') : url('/admin/booking/PrintBarcode')
    }?${stringifySearch({
      createdAt: item.time,
      d2dNo,
    })}`;

    const printArrivalNoticeUrl = `${url(
      '/utilities/Bookings/ExportArrivalNotice',
    )}?${stringifySearch({
      d2dNo,
      isClient,
    })}`;

    const seawayBillUrl = `${
      isClient
        ? url('/utilities/Bookings/ExportSeaWaybill')
        : url('/admin/booking/ExportSeaWaybill')
    }?${stringifySearch({
      id: bookingId,
      d2dNo,
    })}`;

    const invoiceUrl =
      'invoice_id' in item
        ? isClient
          ? `/invoice/${item.invoice_id}/download/`
          : `${url('/admin/booking/InvoiceDetail')}?d2dNo=${d2dNo}&id=${item.invoice_id}`
        : undefined;

    const renderFileLink = () => getFileLink(item.file, trackingDataMeta[statusName].fileName);

    const renderAdminEdit = () => (
      <span
        className={clsx(editable ? classes.link : classes.linkDisabled, classes.baseButton)}
        onClick={editable ? () => onClickEdit(statusName, item) : undefined}
      >
        {formStrings.edit}
      </span>
    );

    const renderLink = (
      label: string,
      url: string,
      useNative: boolean = false,
      download: boolean = true,
    ) => (
      <a
        className={clsx(classes.link, classes.baseButton)}
        target="_blank"
        rel="noreferrer"
        href={useNative ? undefined : url}
        onClick={
          useNative
            ? () => downloadUrl({ url, name: label, type: 'application/pdf', download })
            : undefined
        }
      >
        {label}
      </a>
    );

    const ifStatus = (status: TransportStatus) => eq(status, statusName);

    return (
      <div key={i} className={classes.contentsHoverPrimary}>
        <div className={clsx(classes.trackingDataDetailsCell, classes.whitespacePre)}>
          {timeFormatter(new Date(item.time))}
        </div>
        <div className={clsx(classes.trackingDataDetailsCell, classes.whitespacePre)}>
          {statusName}
        </div>
        <div className={clsx(classes.trackingDataDetailsCell)}>
          <TrackingDetailItemNote statusName={statusName} item={item} />
        </div>
        <div className={clsx(classes.trackingDataDetailsCell, classes.whitespacePre)}>
          {ifStatus('Pre-Alert発行') &&
            !preAlert.is_empty &&
            renderLink(strings.preAlert, preAlertUrl)}

          {ifStatus('ラベル発行') && renderLink(strings.barCode, printBarcodeUrl)}

          {ifStatus('中国工場集荷') && renderFileLink()}

          {ifStatus('荷受け倉庫へ到着') && renderFileLink()}

          {ifStatus('輸出通関許可') && renderFileLink()}

          {ifStatus('Sea Waybill発行') &&
            (item.file ? renderFileLink() : renderLink('Sea Waybill', seawayBillUrl))}

          {ifStatus('輸出港より出港') && renderFileLink()}

          {ifStatus('Arrival Notice生成') &&
            renderLink(arrivalNoticeStrings.arrivalNotice, printArrivalNoticeUrl)}

          {ifStatus('倉庫に到着') && renderFileLink()}

          {ifStatus('輸入通関申告') && renderFileLink()}

          {ifStatus('輸入許可') && renderFileLink()}

          {ifStatus('国内配達中') && renderFileLink()}

          {ifStatus('ドライバー情報・送り状') && renderFileLink()}

          {ifStatus('お届け完了') && renderFileLink()}

          {ifStatus('個別保険証券') && renderFileLink()}

          {ifStatus('リアルタイム口座振替通知書') &&
            (item.file?.length === 1
              ? renderLink(trackingDataMeta[statusName].fileName, item.file[0], isClient, !isClient)
              : renderFileLink())}

          {ifStatus('請求書発行') &&
            invoiceUrl &&
            renderLink(strings.invoice, invoiceUrl, isClient, !isClient)}

          {ifStatus('税関検査') && renderFileLink()}
        </div>
        <div className={clsx(classes.trackingDataDetailsCell, classes.whitespacePre)}>
          {![
            'Pre-Alert発行',
            'ラベル発行',
            'Sea Waybill発行',
            'Arrival Notice生成',
            '個別保険証券',
            '請求書発行',
          ].includes(statusName) &&
            !isClient &&
            renderAdminEdit()}
        </div>
      </div>
    );
  };

  const items = useMemo(() => {
    const data = trackingData.data && 'data' in trackingData.data && trackingData.data.data!;
    if (!data) return undefined;

    const itemsWithStatus: ((TrackingDetailItem | DomesticTrackingDetailItem) & {
      status: TransportStatus;
    })[] = [];

    Object.entries(data).forEach(([status, items]) => {
      itemsWithStatus.push(...(items ?? []).map((item: any) => ({ ...item, status })));
    });

    itemsWithStatus.sort((a, b) => b.time.localeCompare(a.time));

    if (data) {
      return itemsWithStatus.map(({ status, ...item }, i) =>
        renderTrackingDetailItem(status, item, i),
      );
    }

    return null;
  }, [trackingData.data]);

  const renderContent = () => (
    <div className={classes.verticalSpace1}>
      <SpinnerModal open={isLoading} />

      {renderDialog()}

      {dialogOpen && (
        <AddTransportStatusDialog
          d2dNo={d2dNo}
          type={dialogType}
          transportStatus={editingTransportStatus}
          editingItem={editingItem}
          onSubmit={async (values) => {
            const {
              transportStatus,
              date,
              time,
              coopName,
              coopTrackingNo,
              carDriver,
              note,
              carNo,
              file,
            } = values;

            const timeZoneId = trackingDataMeta[transportStatus as TransportStatus].id;

            const isoTime = formatISOString(date, time, timeZoneId);

            try {
              await dashboardPatchTrackingData.mutateAsync({
                transport_status: transportStatus as TransportStatus,
                place: '',
                time: isoTime,
                coop_name: coopName,
                coop_tracking_no: coopTrackingNo,
                car_driver: carDriver,
                car_no: carNo,
                file: file?.flatMap((f) => (f.file instanceof File ? f.file : [])),
                note,
                key: dialogType === 'edit' ? editingItem?.key : undefined,
              });
            } finally {
              setDialogOpen(false);
            }
          }}
          open={dialogOpen}
          onClose={() => {
            setDialogType('add');
            setDialogOpen(false);
          }}
          onDelete={async (query: TrackingDataDeleteQuery) =>
            await dashboardDeleteTrackingData.mutateAsync(query)
          }
        />
      )}

      <div className={classes.trackingDataDetailsTable}>
        <div style={{ display: 'contents' }}>
          <div className={classes.trackingDataDetailsHead}>{strings.dateTime}</div>
          <div className={classes.trackingDataDetailsHead}>{strings.status}</div>
          <div className={classes.trackingDataDetailsHead}>{strings.note}</div>
          <div className={classes.trackingDataDetailsHead}>{strings.document}</div>
          <div className={classes.trackingDataDetailsHead}></div>
        </div>

        {items}
      </div>

      {isClient
        ? null
        : canEdit && (
            <>
              <Button
                size="small"
                shape="round"
                text={strings.addTransportStatus}
                color={foldMessagesEither(
                  canEdit,
                  () => 'grey',
                  () => 'primary',
                )}
                onClick={() =>
                  foldMessagesEither(
                    canEdit,
                    ({ messages }) => {
                      confirm({ messages });
                    },
                    () => {
                      setDialogOpen(true);
                    },
                  )
                }
              />
            </>
          )}
    </div>
  );

  return (
    <QueryContainer
      queries={[trackingData]}
      isNotFound={trackingData.data && 'detail' in trackingData.data}
      renderNotFound={() => (
        <div className={clsx(classes.textGray600, classes.middleText)}>{strings.notFound}</div>
      )}
    >
      {renderContent}
    </QueryContainer>
  );
};
